import React, { useState, useEffect } from "react";
import {
    Drawer, Descriptions, Tag, Spin, message, Table, Typography, Divider, Space, Tabs,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { settlementApi } from "../../../../api/settlementApi";
import { MATCH_STATUS_CONFIG } from "../constants";

dayjs.extend(utc);

const { Text } = Typography;

const fmtTs = (v) => {
    if (!v) return "-";
    const d = dayjs.utc(v);
    return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : v;
};

const fmtDate = (v) => {
    if (!v) return "-";
    const d = dayjs.utc(v);
    return d.isValid() ? d.format("YYYY-MM-DD") : v;
};

const fmtDec = (v, digits = 2) => (v != null ? Number(v).toFixed(digits) : "-");

function calcDiffTag(a, b, mode = "exact") {
    if (!a || !b || a === "-" || b === "-") return null;
    if (mode === "time") {
        const s = dayjs.utc(a);
        const m = dayjs.utc(b);
        if (!s.isValid() || !m.isValid()) return null;
        const diffSec = Math.abs(s.diff(m, "second"));
        if (diffSec === 0) return <Tag color="green" style={{ margin: 0 }}>一致</Tag>;
        if (diffSec < 60) return <Tag color="orange" style={{ margin: 0 }}>{diffSec}s</Tag>;
        const mins = Math.round(diffSec / 60);
        if (mins < 60) return <Tag color="orange" style={{ margin: 0 }}>{mins}min</Tag>;
        const h = Math.floor(mins / 60);
        const rm = mins % 60;
        return <Tag color="red" style={{ margin: 0 }}>{h}h{rm > 0 ? rm + "m" : ""}</Tag>;
    }
    const sa = String(a).trim().toUpperCase();
    const sb = String(b).trim().toUpperCase();
    if (sa === sb) return <Tag color="green" style={{ margin: 0 }}>一致</Tag>;
    return <Tag color="red" style={{ margin: 0 }}>不一致</Tag>;
}

const FlightRecordDetailDrawer = ({ open, recordId, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        if (!open || !recordId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        const fetchDetail = async () => {
            setLoading(true);
            try {
                const res = await settlementApi.flightRecord.getDetail(recordId);
                if (!cancelled) setDetail(res.data || res);
            } catch (err) {
                if (!cancelled) message.error(`加载详情失败: ${err.message}`);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchDetail();
        return () => { cancelled = true; };
    }, [open, recordId]);

    if (!open) return null;

    const d = detail;
    const fo = d?.flight_operation;
    const statusCfg = MATCH_STATUS_CONFIG[d?.match_status] || { color: "default", label: d?.match_status };

    const compareRows = d ? [
        { key: "flight_number", label: "航班号",     settlement: d.flight_number_norm || d.flight_number_raw || "-",   fop: fo?.flight_number || "-",   mode: "exact" },
        { key: "flight_date",   label: "航班日期",   settlement: fmtDate(d.flight_date),                              fop: fmtDate(fo?.flight_date),   mode: "exact" },
        { key: "aircraft",      label: "飞机号",     settlement: d.settlement_aircraft_reg || d.aircraft_msn || "-",   fop: fo?.aircraft_reg || "-",    mode: "exact" },
        { key: "dep",           label: "出发站",     settlement: d.settlement_departure_airport || "-",                fop: fo?.departure_airport || "-", mode: "exact" },
        { key: "arr",           label: "到达站",     settlement: d.settlement_arrival_airport || "-",                  fop: fo?.arrival_airport || "-", mode: "exact" },
        { key: "std",           label: "STD",        settlement: "-",                                                 fop: fmtTs(fo?.std),             mode: null },
        { key: "sta",           label: "STA",        settlement: "-",                                                 fop: fmtTs(fo?.sta),             mode: null },
        { key: "off",           label: "Off (Block-off)",  settlement: fmtTs(d.settlement_atd),       fop: fmtTs(fo?.atd),       mode: "time" },
        { key: "out",           label: "Out (Airborne)",   settlement: fmtTs(d.settlement_atd_icao),  fop: fmtTs(fo?.atd_icao),  mode: "time" },
        { key: "in",            label: "In (Touchdown)",   settlement: fmtTs(d.settlement_ata_icao),  fop: fmtTs(fo?.ata_icao),  mode: "time" },
        { key: "on",            label: "On (Block-in)",    settlement: fmtTs(d.settlement_ata),       fop: fmtTs(fo?.ata),       mode: "time" },
        { key: "bh",            label: "BH",         settlement: d.bh_hhmm ? `${d.bh_hhmm} (${fmtDec(d.bh_decimal, 4)})` : fmtDec(d.settlement_bh), fop: "-", mode: null },
        { key: "fh",            label: "FH",         settlement: d.fh_hhmm ? `${d.fh_hhmm} (${fmtDec(d.fh_decimal, 4)})` : fmtDec(d.settlement_fh), fop: "-", mode: null },
        { key: "cycle",         label: "Cycle",      settlement: d.cycle ?? "-", fop: "-", mode: null },
        { key: "status",        label: "航班状态",   settlement: "-",  fop: fo?.status || "-", mode: null },
    ] : [];

    const compareColumns = [
        {
            title: "字段",
            dataIndex: "label",
            width: 130,
            render: (v) => <Text strong>{v}</Text>,
        },
        {
            title: "结算 (Excel)",
            dataIndex: "settlement",
            width: 200,
            render: (v) => v === "-" ? <Text type="secondary">-</Text> : v,
        },
        {
            title: "FOP (运营)",
            dataIndex: "fop",
            width: 200,
            render: (v, row) => {
                if (!fo) return <Text type="secondary">未匹配</Text>;
                if (v === "-") return <Text type="secondary">-</Text>;
                return v;
            },
        },
        {
            title: "对比",
            width: 80,
            render: (_, row) => {
                if (!fo || !row.mode) return null;
                const rawS = row.settlement;
                const rawF = row.fop;
                if (rawS === "-" || rawF === "-") return null;
                return calcDiffTag(
                    row.mode === "time" ? (row.key === "off" ? d.settlement_atd : row.key === "out" ? d.settlement_atd_icao : row.key === "in" ? d.settlement_ata_icao : d.settlement_ata) : rawS,
                    row.mode === "time" ? (row.key === "off" ? fo.atd : row.key === "out" ? fo.atd_icao : row.key === "in" ? fo.ata_icao : fo.ata) : rawF,
                    row.mode,
                );
            },
        },
    ];

    const tabItems = [
        {
            key: "compare",
            label: "对比总览",
            children: d ? (
                <>
                    <Space style={{ marginBottom: 12 }}>
                        <Text>匹配状态：</Text>
                        <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                        {d.match_detail && (
                            <Text type="secondary" style={{ fontSize: 12 }}>{d.match_detail}</Text>
                        )}
                    </Space>
                    <Table
                        size="small"
                        columns={compareColumns}
                        dataSource={compareRows}
                        pagination={false}
                        bordered
                        rowClassName={(row) => {
                            if (!fo || !row.mode) return "";
                            const rawS = row.settlement;
                            const rawF = row.fop;
                            if (rawS === "-" || rawF === "-") return "";
                            if (row.mode === "exact") {
                                return String(rawS).trim().toUpperCase() !== String(rawF).trim().toUpperCase() ? "ant-table-row-warning" : "";
                            }
                            return "";
                        }}
                    />
                    {fo && (
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                FOP ID: {fo.id} | 机型: {fo.aircraft_type || "-"} | 性质: {fo.stc_type || "-"}
                            </Text>
                        </div>
                    )}
                </>
            ) : null,
        },
        {
            key: "raw",
            label: "原始数据 (23列)",
            children: d ? (
                <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="航班号(原始)">{d.flight_number_raw || "-"}</Descriptions.Item>
                    <Descriptions.Item label="航班号(归一化)">{d.flight_number_norm || "-"}</Descriptions.Item>
                    <Descriptions.Item label="航班日期">{d.flight_date || "-"}</Descriptions.Item>
                    <Descriptions.Item label="航段">{d.segment || "-"}</Descriptions.Item>
                    <Descriptions.Item label="出发站">{d.settlement_departure_airport || "-"}</Descriptions.Item>
                    <Descriptions.Item label="到达站">{d.settlement_arrival_airport || "-"}</Descriptions.Item>
                    <Descriptions.Item label="飞机 (MSN)">{d.aircraft_msn || "-"}</Descriptions.Item>
                    <Descriptions.Item label="飞机 (匹配)">{d.settlement_aircraft_reg || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Off (TIME)">{d.time_off || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Out (TIME)">{d.time_out || "-"}</Descriptions.Item>
                    <Descriptions.Item label="In (TIME)">{d.time_in || "-"}</Descriptions.Item>
                    <Descriptions.Item label="On (TIME)">{d.time_on || "-"}</Descriptions.Item>
                    <Descriptions.Item label="BH (时分)">{d.bh_hhmm || "-"}</Descriptions.Item>
                    <Descriptions.Item label="BH (小数)">{fmtDec(d.bh_decimal, 4)}</Descriptions.Item>
                    <Descriptions.Item label="FH (时分)">{d.fh_hhmm || "-"}</Descriptions.Item>
                    <Descriptions.Item label="FH (小数)">{fmtDec(d.fh_decimal, 4)}</Descriptions.Item>
                    <Descriptions.Item label="Cycle">{d.cycle ?? "-"}</Descriptions.Item>
                    <Descriptions.Item label="燃油 (kg)">{fmtDec(d.fuel_kg)}</Descriptions.Item>
                    <Descriptions.Item label="燃油 (升)">{fmtDec(d.fuel_liter)}</Descriptions.Item>
                    <Descriptions.Item label="燃油密度">{fmtDec(d.fuel_density, 4)}</Descriptions.Item>
                    <Descriptions.Item label="加油量">{fmtDec(d.fuel_uplift)}</Descriptions.Item>
                    <Descriptions.Item label="前段剩油">{fmtDec(d.fuel_previous)}</Descriptions.Item>
                    <Descriptions.Item label="燃油校验 (kg)">{fmtDec(d.fuel_check_kg)}</Descriptions.Item>
                </Descriptions>
            ) : null,
        },
        {
            key: "match",
            label: "匹配详情",
            children: d ? (
                <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="匹配状态">
                        <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="匹配说明" span={2}>{d.match_detail || "-"}</Descriptions.Item>
                    <Descriptions.Item label="关联航班ID">{d.flight_operation_id || "-"}</Descriptions.Item>
                    <Descriptions.Item label="导入批次">{d.import_batch_id || "-"}</Descriptions.Item>
                    <Descriptions.Item label="源文件">{d.source_file || "-"}</Descriptions.Item>
                    <Descriptions.Item label="源文件行号">{d.source_row_number ?? "-"}</Descriptions.Item>
                </Descriptions>
            ) : null,
        },
    ];

    return (
        <Drawer
            title={
                <Space>
                    <span>飞行记录详情</span>
                    {d && (
                        <Text type="secondary" style={{ fontSize: 14 }}>
                            {d.flight_number_norm || d.flight_number_raw} | {d.flight_date} | {d.settlement_departure_airport}→{d.settlement_arrival_airport}
                        </Text>
                    )}
                </Space>
            }
            open={open}
            onClose={onClose}
            width={780}
            destroyOnClose
        >
            <style>{`
                .ant-table-row-warning td { background: #fff7e6 !important; }
            `}</style>
            {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin tip="加载中..." />
                </div>
            ) : d ? (
                <Tabs items={tabItems} defaultActiveKey="compare" size="small" />
            ) : (
                <Text type="secondary">无数据</Text>
            )}
        </Drawer>
    );
};

export default FlightRecordDetailDrawer;
